<template>

  <b-overlay :show="loading">

    <b-breadcrumb>
      <b-breadcrumb-item>Administratie</b-breadcrumb-item>
      <b-breadcrumb-item to="/main/admin/contracts">Contracten</b-breadcrumb-item>
      <b-breadcrumb-item :to="`/main/admin/contracts/view/${contract.id}`">Contract {{contract.name}}</b-breadcrumb-item>
      <b-breadcrumb-item active>Factuur maken</b-breadcrumb-item>
    </b-breadcrumb>

    <h3>Factuur voor contract {{contract.name}} ({{contract.customer.name}})</h3>

    <template v-show="view === 'input'">

      <h4>Stand</h4>

      <div class="ml-3">
        <div class="d-flex align-items-center mb-3">
          1. Vorig:
          <div class="ml-2">
            <template v-if="contract.invoices.nodes.length === 0">
              <span>Geen vorige facturen gevonden van dit contract</span>
            </template>
            <template v-else>
              <b-select class="w-auto" v-model="previousInvoiceId">
                <b-select-option v-for="invoice in contract.invoices.nodes" :value="invoice.id" :key="invoice.id">
                  Q{{invoice.quarter}} {{invoice.year}} | {{invoice.title}}
                </b-select-option>
              </b-select>
            </template>
          </div>
        </div>

        <div class="d-flex align-items-center mb-3">
          <span>2. Huidig:</span><b-form-datepicker v-model="curDate" style="width: 18em" class="ml-2"/>
          <small class="ml-2">{{curDate|format_moment('DD/MM/YYYY')}}</small>
          <b-btn variant="link" @click="setCurToToday">Instellen op vandaag</b-btn>
        </div>

        <div class="d-flex align-items-center mb-3">
          3. 
          <b-btn variant="outline-primary" @click="fetchCounters" class="ml-2">
            <span class="mdi mdi-flash" /> Toestand vorige factuur ophalen & tellerstanden invullen
          </b-btn>
        </div>
        
        <div class="d-flex align-items-center mb-3">
          4. BTW tarief:
          <b-select :options="tlTaxRates" text-field="description" value-field="localId" class="ml-2 w-auto" v-model="taxRate" />
        </div>
        
      </div>
      
      <h4>Afrekenen</h4>

      <div class="pt-2 mb-3 ml-3">
        Vorige factuur geselecteerd:
        <span v-if="previousInvoice === undefined">geen</span>
        <span v-else>Q{{previousInvoice.quarter}} {{previousInvoice.year}} {{previousInvoice.title}}</span>
      </div>
      
      <form id="invoiceForm">

        <div class="form-inline" style="margin-left: 1em;">
          <div class="form-group">
            <label>Afrekening van kwartaal</label>
            <b-select :options="$options.quarters" v-model="quarter" class="ml-1"></b-select>
            <label class="ml-1">van het jaar</label>
            <b-input type="number" v-model.number="year" class="ml-1"/>
          </div>
        </div>

        <h4>Forfaits</h4>

        <div class="ml-3">
          <b-check v-model="noNewForfait">
            Geen nieuw forfait aanrekenen voor volgend kwartaal (einde contract)
          </b-check>
        </div>

        <div class="ml-3 mt-2">
          <h5>Forfait reeds gefactureerd van vorige periode</h5>

          <div class="ml-2">
            <b-check v-model="firstForfait">
              Forfait van start van het contract aanrekenen
              (rekent aantal dagen van start van het contract tot begin er op volgend kwartaal)
            </b-check>

            <table class="table table-bordered table-sm w-auto mt-1">
              <thead>
                <tr>
                  <th>Weergavenaam</th>
                  <th>Teller groep</th>
                  <th>Aantal</th>
                  <th>Prijs</th>
                </tr>
              </thead>
              <tbody v-for="(tuple, i) in contract.tuples.nodes" :key="tuple.id">
                <tr>
                  <td>{{tuple.displayName}}</td>
                  <td>{{tuple.cgroup.displayName}}</td>
                  <td>
                    <b-input type="text" :disabled="firstForfait" v-model.number="alreadyInvoiced[i].numPrints" />
                  </td>
                  <td>
                    <b-input-group prepend="€">
                      <b-input type="number" step="0.0001" v-model.number="alreadyInvoiced[i].price" />
                    </b-input-group>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>


        <h4>Tellerstanden</h4>

        <table class="table table-bordered table-sm table-vcenter table-nopadding table-striped invoice-counter-input mt-2 ml-3" style="width: auto;">
          <thead>
            <tr>
              <th>Merk/naam</th>
              <th>Serienr</th>
              <template v-for="x in cgroupMap.size">
                <th :key="`teller_${x}`">Teller</th>
                <th :key="`huidig_${x}`">Huidig</th>
                <th :key="`vorig_${x}`">Vorig</th>
              </template>
            </tr>
          </thead>


          <tbody>
            <tr v-for="({printer, tuples}, i) in contract.contractPrinters.nodes" :key="printer.id">
              <td class="text-nowrap">{{printer.model.brand.name}} {{printer.model.name}}</td>
              <td>{{printer.serial}}</td>

              <template v-for="(cgroup, j) in cgroupMap.values()">

                <template v-if="tuples.nodes.findIndex(t => t.cgroup.id === cgroup.id) === -1">
                  <td :key="`a_${cgroup.id}`"></td>
                  <td :key="`c_${cgroup.id}`"></td>
                  <td :key="`b_${cgroup.id}`"></td>
                </template>
                <template v-else>
                  <td :key="`a_${cgroup.id}`">{{cgroup.displayName}}</td>
                  <td :key="`c_${cgroup.id}`">
                    <b-input-group class="flex-nowrap">
                      <b-form-input v-model.number="valuesMap[`${cgroup.id}@${printer.id}`].cur" class="counter-input" />
                      <template #append>
                        <b-popover :target="`i_${i}_${j}`" triggers="click" title="Tellerbron" placement="top">
                          <span v-html="valuesMap[`${cgroup.id}@${printer.id}`].sourceCur"/>
                        </b-popover>
                        <b-button :id="`i_${i}_${j}`" :variant="valuesMap[`${cgroup.id}@${printer.id}`].variantCur">
                          <span class="mdi" :class="valuesMap[`${cgroup.id}@${printer.id}`].iconCur"/>
                        </b-button>
                      </template>
                    </b-input-group>
                  </td>
                  <td :key="`b_${cgroup.id}`">
                    <b-input-group class="flex-nowrap">
                      <b-form-input v-model.number="valuesMap[`${cgroup.id}@${printer.id}`].prev" class="counter-input" />
                      <template #append>
                        <b-popover :target="`i2_${i}_${j}`" triggers="click" title="Tellerbron" placement="top">
                          <span v-html="valuesMap[`${cgroup.id}@${printer.id}`].sourcePrev"/>
                        </b-popover>
                        <b-button :id="`i2_${i}_${j}`" variant="outline-info">
                          <span class="mdi" :class="valuesMap[`${cgroup.id}@${printer.id}`].iconPrev"/>
                        </b-button>
                      </template>
                    </b-input-group>
                  </td>
                </template>
              </template>


            </tr>
          </tbody>
        </table>
      </form>

      <h4>Factuur genereren</h4>

      <p class="ml-2">
        <b-btn variant="outline-primary" @click="generateInvoice"><b-icon icon="gear"/> Factuur genereren</b-btn>
      </p>
    </template>

    <template v-if="view === 'preview'">
      <h4>{{previewTitle}}</h4>
      <p class="ml-2">
        <busy-btn :busy="storeBusy" variant="primary" @click="store"><span class="mdi mdi-cloud-upload-outline" /> Naar Teamleader factuur</busy-btn>
        <!--        <button class="btn btn-outline-primary btn-sm" data-bind="click: btnToTLCreditnote, visible: invoice.total < 0"><i class="fa fa-fw fa-cloud-upload"></i> Naar Teamleader creditnota</button>-->
      </p>
      <invoice-preview :grouped-lines="previewGroupedLines" class="ml-2" />

    </template>

  </b-overlay>

</template>

<script>
import clone from 'stringify-clone';
import omitDeep from 'omit-deep-lodash';

import BusyBtn from '@/components/BusyBtn';

import previous_quarter from '@/lib/previous_quarter';
import {CACHE_FIRST, NETWORK_ONLY} from '@/lib/constants';
import quarter_start from '@/lib/quarter_start';
import quarter_end from '@/lib/quarter_end';
import moment from 'moment';
import InvoicePreview from '@/components/InvoicePreview';
import {next_quarter} from '@/lib/next_quarter';

function cgroup_input(cgroup, printer)
{
    return {
        cgroup,
        cur: 0,
        foundCur: false,
        foundPrev: false,
        prev: 0,
        printer,
        sourceCur: 'Manuele ingave',
        sourcePrev: 'Manuele ingave',
        iconCur: 'mdi-information-outline',
        iconPrev: 'mdi-information-outline',
        variantCur: 'light'
    }
}

const default_tax_id = ['TL', '52346c10-6e22-00ae-814f-42a7fdb6c381'];

export default
{
    apollo:
    {
        forRead:
        {
            query: require('../../graphql/createInvoice_getContract.gql'),
            variables() { return { id: this.id } },
            fetchPolicy: NETWORK_ONLY,
            update({contract})
            {
                this.contract         = contract;

                this.alreadyInvoiced = contract.tuples.nodes.map(
                    t => ({tupleId: t.tupleId, numPrints: t.forfaitNumPrints, price: t.forfaitPrice})
                );

                const cgroupMap = new Map();
                const valuesMap = {};

                contract.tuples.nodes.forEach(
                    ({cgroup, appliesTo}) =>
                    {
                        cgroupMap.set(cgroup.id, cgroup);

                        appliesTo.nodes.forEach(
                            ({printer}) =>
                                valuesMap[`${cgroup.id}@${printer.id}`] = cgroup_input(cgroup.id, printer.id)
                        );
                    }
                );

                this.cgroupMap = cgroupMap;
                this.valuesMap = valuesMap;
                
                if(contract.invoices.nodes.length > 0)
                    this.previousInvoiceId = contract.invoices.nodes[0].id;
                else
                    this.previousInvoiceId = null;
                
                this.taxRate = contract.customer.sourceTaxRateId || default_tax_id;
                
                this.loading = false;
            }
        },
        tlTaxRates:
        {
            query: require('../../graphql/query/taxRates.gql'),
            fetchPolicy: CACHE_FIRST
        }
    },
    components: { BusyBtn, InvoicePreview  },
    computed:
    {
        noNewForfait:
        {
            get() { return !this.newForfait },
            set(v) { this.newForfait = !v }
        }
    },
    data()
    {
        const q = previous_quarter(new Date(), 1);
        const q2 = previous_quarter(new Date(), 2);

        return {
            alreadyInvoiced: [],
            contract: { tuples: {nodes:[]}, customer: {}, contractPrinters: { nodes: [] }, invoices: { nodes: [] } },
            cgroupMap: new Map(),
            quarter: q.q,
            year: q.year,
            newForfait: true,
            firstForfait: false,
            curDate: quarter_end(q.year, q.q),
            prevDate: quarter_start(q.year, q.q),
            previousInvoice: undefined,
            previousInvoiceId: null,
            tlTaxRates: [],
            taxRate: null,
            tuples: [],
            storeBusy: false,
            valuesMap: {},
            fetchCounterBusy: false,
            // Preview
            previewGroupedLines: [],
            view: 'input',
            previewTitle: '',
            invoiceData: null,
            loading: true
        }
    },
    methods:
    {
        async fetchCountersPrev()
        {
            const {data: { invoiceById } } = await this.$apollo.query(
                {
                    query: require('../../graphql/query/fetch_counters.gql'),
                    variables: { id: this.previousInvoiceId },
                    fetchPolicy: NETWORK_ONLY
                }
            );

            if(invoiceById === null)
            {
                this.previousInvoice = null;
                return;
            }

            const invoice = invoiceById;

            invoice.invoiceCounters.nodes.forEach(
                counter =>
                {
                    const entry = this.valuesMap[`${counter.cgroup.id}@${counter.printer.id}`];

                    if(entry === undefined) return;

                    entry.prev = counter.cur;
                    entry.sourcePrev = `Factuur<br/>${invoice.title}<br><br>Stand van: ${moment(invoice.curCounter).format('YYYY-MM-DD')}`;
                    entry.iconPrev = 'mdi-check-bold';
                    entry.foundPrev = true;
                }
            );

            this.previousInvoice = invoice;

            const next = next_quarter(invoice.quarter, invoice.year, 1);

            this.quarter  = next.q;
            this.year     = next.year;

            invoice.invoiceForfaits.nodes.forEach(
                forfait =>
                {
                    const f = this.alreadyInvoiced.find(ai => ai.tupleId === forfait.tupleId);
                    f.numPrints = forfait.numPrints;
                    f.price = forfait.price;
                }
            );
        },
        async fetchCountersCur()
        {
            const greaterThanOrEqualTo  = moment(this.curDate).subtract(3, 'day').startOf('day').valueOf();
            const lessThanOrEqualTo     = moment(this.curDate).endOf('day').valueOf();

            const result = this.contract.contractPrinters.nodes.map(
                async ({printer: {id}}) =>
                {
                    const {data: {printerById: {histories}}} = await this.$apollo.query(
                        {
                            query: require('../../graphql/query/fetch_counters_cur.gql'),
                            variables:
                            {
                                id,
                                filter:
                                {
                                    tsms: { greaterThanOrEqualTo, lessThanOrEqualTo },
                                    cgroup: { type: { equalTo: 'COUNTER' } }
                                }
                            },
                            fetchPolicy: NETWORK_ONLY
                        }
                    );

                    histories.nodes.forEach(
                        history =>
                        {
                            const entry = this.valuesMap[`${history.cgroup.id}@${id}`];

                            if(entry === undefined) return;

                            entry.cur = history.value;
                            entry.sourceCur = `Tellerstand (telemetrie)<br/>Stand van: ${moment(history.tsms).format('YYYY-MM-DD')}`;
                            entry.iconCur = 'mdi-check-bold';
                            entry.foundCur = true;
                        }
                    );
                }
            );

            const ret = await Promise.all(result);
            
            Object.keys(this.valuesMap).forEach(
                key =>
                {
                    this.valuesMap[key].variantCur = this.valuesMap[key].foundCur ? 'light' : 'danger' 
                }
            );
            
            return ret;
        },
        async fetchCounters()
        {
            this.fetchCounterBusy = true;

            try
            {
                await Promise.allSettled(
                    [
                        this.fetchCountersPrev(),
                        this.fetchCountersCur()
                    ]
                );

                for (const [, value] of Object.entries(this.valuesMap))
                {
                    if(!value.foundCur)
                    {
                        value.iconCur = 'mdi-emoticon-sad-outline';
                    }

                    if(!value.foundPrev)
                    {
                        value.iconPrev = 'mdi-emoticon-sad-outline';
                    }
                }
            }
            catch (ex)
            {
                console.error(ex);
            }
            finally
            {
                this.fetchCounterBusy = false;
            }
        },
        async generateInvoice()
        {
            const { data } = await this.$apollo.mutate(
                {
                    mutation: require('../../graphql/mutation/generateInvoice.gql'),
                    variables:
                    {
                        input:
                        {
                            alreadyInvoiced: this.alreadyInvoiced,
                            counters: Object.values(this.valuesMap)
                                .map(v =>
                                    (
                                        {
                                            printer: v.printer,
                                            cgroup: v.cgroup,
                                            cur: v.cur,
                                            prev: v.prev
                                        }
                                    )
                                ),
                            cur: moment(this.curDate).valueOf(),
                            firstForfait: this.firstForfait,
                            prev: moment(this.prevDate).valueOf(),
                            id: this.id,
                            newForfait: this.newForfait,
                            quarter: this.quarter,
                            taxRate: this.taxRate,
                            year: this.year
                        }
                    }
                }
            );

            const cloned = omitDeep(clone(data.generateInvoice), '__typename');

            this.invoiceData = Object.freeze(cloned);

            this.previewGroupedLines  = this.invoiceData.data.grouped_lines;
            this.previewTitle         = data.title;

            this.view = 'preview';
        },
        async store()
        {
            this.storeBusy = true;

            try
            {
                const { data } = await this.$apollo.mutate(
                    {
                        mutation: require('../../graphql/mutation/store_invoice.gql'),
                        variables:
                        {
                            input: this.invoiceData,
                            customerId: this.contract.customer.id,
                            taxRate: this.taxRate
                        }
                    }
                );

                const contractId = data.storeInvoice.invoice.contract.id;

                await this.$router.replace(`/main/admin/contracts/view/${contractId}`);
            }
            catch (ex)
            {
                console.error(ex);
            }

            this.storeBusy = false;
        },
        setCurToToday()
        {
            this.curDate = moment().format('YYYY-MM-DD');
        }
    },
    props:
    {
        id: { type: String, required: true },
    },
    watch:
    {
        previousInvoiceId(val)
        {
            let invoice;
            
            for (invoice of this.contract.invoices.nodes)
            {
                if(invoice.id !== val)
                    continue;

                const next    = next_quarter(invoice.quarter, invoice.year, 1);

                this.curDate  = quarter_end(next.year, next.q);
                this.quarter  = next.q;
                this.year     = next.year;
                
                this.prevDate = invoice.curCounter;
                break;
            }
        }
    },
    quarters:
    [
        { value: 1, text: 'Q1 (factuurdatum 01/04)'},
        { value: 2, text: 'Q2 (factuurdatum 01/07)'},
        { value: 3, text: 'Q3 (factuurdatum 01/10)'},
        { value: 4, text: 'Q4 (factuurdatum 01/01)'}
    ]
}
</script>

<style>


table.invoice-counter-input tbody tr td:nth-child(3),
table.invoice-counter-input tbody tr td:nth-child(4),
table.invoice-counter-input tbody tr td:nth-child(5)
{
    background-color: #DBE3EE !important;
}

table.invoice-counter-input tbody tr td:nth-child(6),
table.invoice-counter-input tbody tr td:nth-child(7),
table.invoice-counter-input tbody tr td:nth-child(8)
{
    background-color: #E6DBEE !important;
}

table.invoice-counter-input tbody tr td:nth-child(9),
table.invoice-counter-input tbody tr td:nth-child(10),
table.invoice-counter-input tbody tr td:nth-child(11)
{
    background-color: #EEDBDB !important;
}

table.invoice-counter-input tbody tr td:nth-child(12),
table.invoice-counter-input tbody tr td:nth-child(13),
table.invoice-counter-input tbody tr td:nth-child(14)
{
    background-color: #EEEEDB !important;
}

.counter-input
{
    font-family: Consolas, monospace;
    text-align: right !important;
    font-size: 14px !important;
    width: 6em !important;
}

</style>